import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import styled from 'styled-components'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, ONE_DAY_BEFORE_CHECK_IN, TWO_DAYS_BEFORE_CHECK_IN, THREE_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE, SEVEN_DAYS_BEFORE_CHECK_IN, THIRTY_ONE_DAYS_BEFORE_CHECK_IN, TWENTY_ONE_DAYS_BEFORE_CHECK_IN, TWENTY_EIGHT_DAYS_BEFORE_CHECK_IN, FORTY_FIVE_DAYS_BEFORE_CHECK_IN, SIXTY_DAYS_BEFORE_CHECK_IN, NINETY_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import ModalContext from 'contexts/ModalContext'
import AlwaysOnOfferPropertyCancellationPolicyModal from './AlwaysOnOfferPropertyCancellationPolicyModal'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicy: App.OfferCancellationPolicy
  checkInDate?: string | moment.Moment
  className?: string
  propertyTimezoneOffset?: number
  hideNonRefundableMessage?: boolean
  textLinkFormat?: boolean
}

function AlwaysOnOfferPropertyCancellationPolicies(props: Props) {
  const {
    cancellationPolicy,
    checkInDate,
    className,
    propertyTimezoneOffset,
    hideNonRefundableMessage,
    textLinkFormat,
  } = props
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const standardDetail = intl.formatMessage({
    id: 'amo5QV9y/A',
    defaultMessage: 'Standard bookings can be cancelled with a full refund provided the cancellation is made until check-in. That is cancel by 11:59 pm (local time of property) prior to the day of check-in.',
    description: 'Standard cancellation policy detail for AlwaysOn offers',
  })
  const nonRefundableDetail = intl.formatMessage({
    id: '/xoOFpz56P',
    defaultMessage: 'Please note that ‘Non-Refundable’ packages are guaranteed at the time of booking and not eligible for a refund or date change. Accordingly, please select your package carefully and take note of any cancellation policy applicable to your purchase.',
    description: 'Non-refundable package disclaimer for AlwaysOn offers',
  })
  let cancellationPolicyDetail = cancellationPolicy.policyDetail[1] ?? standardDetail

  const days = useMemo(() => {
    switch (cancellationPolicy.type) {
      case 'prior-to-check-in-one-day':
        return ONE_DAY_BEFORE_CHECK_IN
      case 'prior-to-check-in-two-days':
        return TWO_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-three-days':
        return THREE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-seven-days':
        return SEVEN_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-fourteen-days':
        return FOURTEEN_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-twenty-one-days':
      case 'post-purchase-seven-days':
        return TWENTY_ONE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-twenty-eight-days':
        return TWENTY_EIGHT_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-thirty-one-days':
        return THIRTY_ONE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-forty-five-days':
        return FORTY_FIVE_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-sixty-days':
        return SIXTY_DAYS_BEFORE_CHECK_IN
      case 'prior-to-check-in-ninety-days':
        return NINETY_DAYS_BEFORE_CHECK_IN
      default:
        return 0
    }
  }, [cancellationPolicy.type])

  let startDate: moment.Moment | undefined

  const startBasedOnCheckIn = checkInDate ? ((propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days'))) : undefined
  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')

  if (cancellationPolicy.type === 'post-purchase-seven-days') {
    if (!startBasedOnCheckIn) {
      startDate = startBasedOnToday
    } else {
      startDate = startBasedOnToday.isBefore(startBasedOnCheckIn) ? startBasedOnToday : startBasedOnCheckIn
    }
  } else {
    startDate = startBasedOnCheckIn
  }

  const refundable = cancellationPolicy.type !== 'non-refundable' && (!startDate || startDate?.isAfter(moment()))
  if (cancellationPolicy.type !== 'non-refundable' && !refundable) {
    cancellationPolicyDetail = nonRefundableDetail
  }

  const freeCancellationLabel = intl.formatMessage({
    id: 'zLhLXEBbh8',
    defaultMessage: 'Free cancellation',
    description: 'Label for free cancellation on AlwaysOn offers',
  })
  const daysPriorHotelOnlyLabel = (days: number) =>
    intl.formatMessage(
      {
        id: 'tI9eek6Woc',
        defaultMessage: '{days} days prior to check in (Hotel only)',
        description: 'Days prior to check-in for hotel-only cancellation',
      },
      { days },
    )
  const untilDateHotelOnlyLabel = (date: string) =>
    intl.formatMessage(
      {
        id: 'USpFonAQbY',
        defaultMessage: 'until {date} (Hotel only)',
        description: 'Deadline date for hotel-only free cancellation',
      },
      { date },
    )
  const nonRefundableLabel = intl.formatMessage({
    id: 'Y/tav0v2rP',
    defaultMessage: 'Non-refundable',
    description: 'Label for non-refundable AlwaysOn offers',
  })
  const freeCancellationAvailableLabel = intl.formatMessage({
    id: 'B0BkXbhDJd',
    defaultMessage: 'Free cancellation available',
    description: 'Label when free cancellation is available on AlwaysOn offers',
  })
  const untilDateLabel = (date: string) =>
    intl.formatMessage(
      {
        id: 'emxHsxkIFW',
        defaultMessage: 'until {date}',
        description: 'Deadline date for free cancellation',
      },
      { date },
    )

  const showModal = useContext(ModalContext)

  const onClickIcon = useCallback(() => {
    showModal(<AlwaysOnOfferPropertyCancellationPolicyModal refundable={refundable} cancellationPolicyDetail={cancellationPolicyDetail} />)
  }, [showModal, refundable, cancellationPolicyDetail])

  return (
    <Clickable onClick={onClickIcon} className={className}>
      {!hideNonRefundableMessage && (
        <>
          {refundable && (
            <>
              {textLinkFormat &&
                <TextLink
                  size="medium"
                  colour="highlight-secondary"
                  underline={false}
                  startIcon={<LineRefundIcon />}
                >
                  {freeCancellationLabel}
                </TextLink>}
              {!textLinkFormat && (
                <>
                  <BodyText variant="medium" weight="bold" as="span" colour="highlight-secondary">{freeCancellationLabel}</BodyText>{' '}
                  {!startDate && Boolean(days) && <BodyText variant="medium" as="span">{daysPriorHotelOnlyLabel(days)}</BodyText>}
                  {startDate && <BodyText variant="medium" as="span">{untilDateHotelOnlyLabel(intlDateFormatter(startDate, INTL_SHORT_DAY_NAME_DAY_MONTH))}</BodyText>}
                  {' '}<MiddledTooltipIcon size="XS" />
                </>
              )}
            </>
          )}
          {!refundable && cancellationPolicy?.type !== 'hidden-cancellation-policy' && (
            <>
              <BodyText variant="medium" as="span">{nonRefundableLabel}</BodyText>
              {' '}<MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
      {hideNonRefundableMessage && refundable && (
        <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              colour="highlight-secondary"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {freeCancellationAvailableLabel}
            </TextLink>}
          {!textLinkFormat && (
            <>
              <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">{freeCancellationAvailableLabel}</BodyText>{' '}
              {startDate &&
                <BodyText variant="medium" as="span">{untilDateLabel(intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT))}</BodyText>}
              {' '}<MiddledTooltipIcon size="XS" />
            </>
          )}
        </>
      )}
    </Clickable>
  )
}

export default AlwaysOnOfferPropertyCancellationPolicies
